import { COUNTRIES_DATASET } from "@/data/countries";
import { INDUSTRIES_DATASET } from "@/data/industries";

/** Arabic normalisation — strips diacritics, unifies alef/ya/ta-marbuta forms. */
export const normalizeArabic = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .trim()
    // Remove diacritics (tashkeel) and tatweel
    .replace(/[\u064B-\u065F\u0670\u0640]/g, "")
    // Alef variants → bare alef
    .replace(/[إأآٱ]/g, "ا")
    // Ya variants
    .replace(/[ىي]/g, "ي")
    // Ta marbuta → ha (loose match)
    .replace(/ة/g, "ه")
    // Hamza on line
    .replace(/ؤ/g, "و")
    .replace(/ئ/g, "ي")
    // Collapse whitespace
    .replace(/\s+/g, " ");

/** Build a lookup of aliases → canonical Arabic name (country / industry). */
const buildAliasMap = () => {
  const map = new Map();
  for (const c of COUNTRIES_DATASET) {
    map.set(normalizeArabic(c.name), c.name);
    map.set(normalizeArabic(c.nameEn), c.name);
    map.set(normalizeArabic(c.isoCode), c.name);
  }
  for (const i of INDUSTRIES_DATASET) {
    map.set(normalizeArabic(i.name), i.name);
    map.set(normalizeArabic(i.nameEn), i.name);
    for (const a of i.aliases || []) {
      map.set(normalizeArabic(a), i.name);
    }
  }
  return map;
};

const ALIAS_MAP = buildAliasMap();

/** Extra light synonym expansion for common Arabic economic terms. */
const SYNONYMS = {
  استثمار: ["تمويل", "جولة"],
  استحواذ: ["شراء", "تملّك"],
  شراكة: ["تعاون", "تحالف"],
  مناقصة: ["عطاء"],
  مشروع: ["فرصه"],
  فرصة: ["فرصه"],
  ذكاء: ["ai"],
  طاقة: ["نفط", "غاز", "شمسية"],
  صحة: ["طبي", "healthcare"],
};

const expandTerms = (term) => {
  const t = normalizeArabic(term);
  const set = new Set([t]);
  for (const [key, values] of Object.entries(SYNONYMS)) {
    const n = normalizeArabic(key);
    if (t === n || t.includes(n)) {
      values.forEach((v) => set.add(normalizeArabic(v)));
    }
    for (const v of values) {
      if (t === normalizeArabic(v)) set.add(n);
    }
  }
  // Alias resolution: if user typed an English or ISO alias, add canonical.
  if (ALIAS_MAP.has(t)) set.add(normalizeArabic(ALIAS_MAP.get(t)));
  return Array.from(set);
};

/**
 * Intelligent search matcher.
 * Returns true if ANY token from the query matches the opportunity haystack,
 * with normalisation + alias resolution + light synonym expansion.
 */
export const matchesQuery = (opportunity, query) => {
  const q = (query || "").trim();
  if (!q) return true;

  const haystack = normalizeArabic(
    `${opportunity.title} ${opportunity.summary} ${opportunity.country} ${opportunity.industry} ${opportunity.type}`,
  );

  // Tokenise on whitespace; every token must match somewhere (AND semantics).
  const tokens = q
    .split(/\s+/)
    .map((t) => t.trim())
    .filter(Boolean);

  return tokens.every((token) => {
    const candidates = expandTerms(token);
    return candidates.some((c) => c.length >= 2 && haystack.includes(c));
  });
};
